import React, { useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Lock as LockIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import {
  ALL_PERMISSIONS,
  rolePermissions,
  getEffectivePermissions,
  loadOverrides,
  saveOverrides,
  clearOverrides,
} from '../../utils/accessControl';

// ─── Static visual config ─────────────────────────────────────────────────────

const ROLE_ORDER = ['admin', 'faculty', 'cbcfaculty', 'student'];

const roleColor = {
  admin:      'error',
  faculty:    'primary',
  cbcfaculty: 'secondary',
  student:    'default',
};

const roleLabel = {
  admin:      'Admin',
  faculty:    'Faculty',
  cbcfaculty: 'CBC Faculty',
  student:    'Student',
};

const permissionCategory = {
  MANAGE_USERS:          'User Management',
  VIEW_USERS:            'User Management',
  CREATE_USERS:          'User Management',
  EDIT_USERS:            'User Management',
  DELETE_USERS:          'User Management',
  RESET_PASSWORD:        'User Management',
  TOGGLE_USER_STATUS:    'User Management',
  MANAGE_PLATFORMS:      'Platform Config',
  VIEW_PLATFORM_ALERTS:  'Platform Config',
  MANAGE_BATCHES:        'Coding Tracker',
  VIEW_BATCHES:          'Coding Tracker',
  MANAGE_STUDENTS:       'Coding Tracker',
  VIEW_STUDENTS:         'Coding Tracker',
  CREATE_ASSIGNMENT:     'Coding Tracker',
  EDIT_ASSIGNMENT:       'Coding Tracker',
  DELETE_ASSIGNMENT:     'Coding Tracker',
  VIEW_ASSIGNMENTS:      'Coding Tracker',
  VIEW_SUBMISSIONS:      'Submissions',
  SUBMIT_SOLUTION:       'Submissions',
  RUN_PLAGIARISM_CHECK:  'Plagiarism',
  VIEW_PLAGIARISM_RESULTS: 'Plagiarism',
  VIEW_BATCH_DASHBOARD:  'Grading',
  GRADE_SUBMISSIONS:     'Grading',
  EDIT_OWN_PROFILE:      'Profile',
};

const categoryColor = {
  'User Management': 'error',
  'Platform Config': 'warning',
  'Coding Tracker':  'info',
  'Submissions':     'success',
  'Plagiarism':      'secondary',
  'Grading':         'primary',
  'Profile':         'default',
};

/** ALL_PERMISSIONS grouped by category in display order */
const GROUPED_PERMISSIONS = ALL_PERMISSIONS.reduce((acc, perm) => {
  const cat = permissionCategory[perm] ?? 'Other';
  if (!acc[cat]) acc[cat] = [];
  acc[cat].push(perm);
  return acc;
}, {});

const CATEGORY_ORDER = [
  'User Management', 'Platform Config', 'Coding Tracker',
  'Submissions', 'Plagiarism', 'Grading', 'Profile',
];

/** SCREAMING_SNAKE → Title Case */
const permLabel = (perm) =>
  perm.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

// ─── Helper: build state maps ─────────────────────────────────────────────────

const buildMap = () =>
  Object.fromEntries(ROLE_ORDER.map((r) => [r, [...getEffectivePermissions(r)]]));

// ─── Sub-component: View mode — permission chip groups ────────────────────────

const PermissionChips = ({ permissions }) => {
  const grouped = permissions.reduce((acc, p) => {
    const cat = permissionCategory[p] ?? 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  return (
    <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {CATEGORY_ORDER.filter((c) => grouped[c]).map((category) => (
        <Box key={category}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {category}
          </Typography>
          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {grouped[category].map((p) => (
              <Chip
                key={p}
                label={permLabel(p)}
                size="small"
                color={categoryColor[category] ?? 'default'}
                variant="outlined"
                sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
              />
            ))}
          </Box>
        </Box>
      ))}
      {permissions.length === 0 && (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">
          No permissions assigned.
        </Typography>
      )}
    </Box>
  );
};

// ─── Sub-component: Edit mode — checkbox groups ────────────────────────────────

const PermissionCheckboxes = ({ draftPerms, onChange }) => (
  <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
    {CATEGORY_ORDER.map((category) => {
      const perms = GROUPED_PERMISSIONS[category];
      if (!perms) return null;

      const allChecked = perms.every((p) => draftPerms.includes(p));
      const someChecked = perms.some((p) => draftPerms.includes(p));

      const handleToggleAll = () => {
        if (allChecked) {
          perms.forEach((p) => draftPerms.includes(p) && onChange(p));
        } else {
          perms.forEach((p) => !draftPerms.includes(p) && onChange(p));
        }
      };

      return (
        <Box key={category}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Checkbox
              size="small"
              checked={allChecked}
              indeterminate={someChecked && !allChecked}
              onChange={handleToggleAll}
              sx={{ p: 0.5 }}
            />
            <Chip
              label={category}
              size="small"
              color={categoryColor[category] ?? 'default'}
              variant="filled"
              sx={{ fontWeight: 700, fontSize: '0.72rem' }}
            />
          </Box>
          <FormGroup sx={{ pl: 3, display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
            {perms.map((perm) => (
              <FormControlLabel
                key={perm}
                control={
                  <Checkbox
                    size="small"
                    checked={draftPerms.includes(perm)}
                    onChange={() => onChange(perm)}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {permLabel(perm)}
                  </Typography>
                }
                sx={{ minWidth: 220, mr: 0 }}
              />
            ))}
          </FormGroup>
        </Box>
      );
    })}
  </Box>
);

// ─── Sub-component: single role row ──────────────────────────────────────────

const RoleRow = ({ role, draftPerms, savedPerms, isEditing, isExpanded, onToggleExpand, onStartEdit, onTogglePerm, onSave, onCancel }) => {
  const isDirty = useMemo(() => {
    if (draftPerms.length !== savedPerms.length) return true;
    const savedSet = new Set(savedPerms);
    return draftPerms.some((p) => !savedSet.has(p));
  }, [draftPerms, savedPerms]);

  return (
    <>
      <TableRow
        hover={!isEditing}
        sx={{ cursor: isEditing ? 'default' : 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
        onClick={() => !isEditing && onToggleExpand()}
      >
        <TableCell sx={{ width: 160 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              label={roleLabel[role] ?? role}
              color={roleColor[role] ?? 'default'}
              size="small"
              variant="filled"
              sx={{ fontWeight: 600, minWidth: 90, justifyContent: 'center' }}
            />
            {isDirty && !isEditing && (
              <Tooltip title="Unsaved changes">
                <Chip label="edited" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem' }} />
              </Tooltip>
            )}
          </Stack>
        </TableCell>

        <TableCell sx={{ width: 130 }}>
          <Chip
            label={`${draftPerms.length} permission${draftPerms.length !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
          />
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            {draftPerms.slice(0, 4).map((p) => (
              <Chip
                key={p}
                label={permLabel(p)}
                size="small"
                variant="outlined"
                color={categoryColor[permissionCategory[p]] ?? 'default'}
                sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
              />
            ))}
            {draftPerms.length > 4 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                +{draftPerms.length - 4} more
              </Typography>
            )}
            {draftPerms.length === 0 && (
              <Typography variant="caption" color="text.disabled" fontStyle="italic">
                No permissions
              </Typography>
            )}
          </Box>
        </TableCell>

        <TableCell sx={{ width: 160 }} align="right" onClick={(e) => e.stopPropagation()}>
          {isEditing ? (
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={onSave} disableElevation>
                Save
              </Button>
              <Button size="small" variant="outlined" startIcon={<CancelIcon />} onClick={onCancel}>
                Cancel
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Tooltip title="Edit permissions for this role">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={(e) => { e.stopPropagation(); onStartEdit(); }}
                >
                  Edit
                </Button>
              </Tooltip>
              <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
                <IconButton size="small" onClick={onToggleExpand}>
                  {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={4} sx={{ p: 0, border: isExpanded || isEditing ? undefined : 'none' }}>
          <Collapse in={isExpanded || isEditing} timeout="auto" unmountOnExit>
            <Divider />
            {isEditing ? (
              <PermissionCheckboxes draftPerms={draftPerms} onChange={(perm) => onTogglePerm(role, perm)} />
            ) : (
              <PermissionChips permissions={draftPerms} />
            )}
            <Divider />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const AccessControlPage = () => {
  const [draftMap, setDraftMap] = useState(buildMap);
  const [savedMap, setSavedMap] = useState(buildMap);
  const [editingRole, setEditingRole] = useState(null);
  const [expandedRole, setExpandedRole] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleToggleExpand = useCallback((role) => {
    setExpandedRole((prev) => (prev === role ? null : role));
  }, []);

  const handleStartEdit = useCallback((role) => {
    setEditingRole(role);
    setExpandedRole(role);
  }, []);

  const handleTogglePerm = useCallback((role, perm) => {
    setDraftMap((prev) => {
      const current = prev[role];
      const next = current.includes(perm)
        ? current.filter((p) => p !== perm)
        : [...current, perm];
      return { ...prev, [role]: next };
    });
  }, []);

  const handleSave = useCallback((role) => {
    const existingOverrides = loadOverrides() ?? {};
    saveOverrides({ ...existingOverrides, [role]: draftMap[role] });
    setSavedMap((prev) => ({ ...prev, [role]: [...draftMap[role]] }));
    setEditingRole(null);
    setSnackbar({ open: true, message: `Permissions for "${roleLabel[role]}" saved.`, severity: 'success' });
  }, [draftMap]);

  const handleCancel = useCallback((role) => {
    setDraftMap((prev) => ({ ...prev, [role]: [...savedMap[role]] }));
    setEditingRole(null);
  }, [savedMap]);

  const handleResetAll = useCallback(() => {
    clearOverrides();
    const defaults = Object.fromEntries(ROLE_ORDER.map((r) => [r, [...(rolePermissions[r] ?? [])]]));
    setDraftMap(defaults);
    setSavedMap(defaults);
    setEditingRole(null);
    setExpandedRole(null);
    setSnackbar({ open: true, message: 'All roles reset to defaults.', severity: 'info' });
  }, []);

  const hasAnyUnsaved = ROLE_ORDER.some((role) => {
    const draft = draftMap[role];
    const saved = savedMap[role];
    if (draft.length !== saved.length) return true;
    const savedSet = new Set(saved);
    return draft.some((p) => !savedSet.has(p));
  });

  const isUsingOverrides = loadOverrides() !== null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LockIcon color="action" />
          <Typography variant="h5" fontWeight={700}>
            Access Control
          </Typography>
          {isUsingOverrides && (
            <Chip label="Custom" size="small" color="warning" variant="outlined" />
          )}
        </Box>
        <Tooltip title="Discard all custom changes and restore original defaults">
          <span>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<ResetIcon />}
              onClick={handleResetAll}
              disabled={!isUsingOverrides && !hasAnyUnsaved}
            >
              Reset to Defaults
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: hasAnyUnsaved ? 1 : 3 }}>
        Configure which permissions each role holds. Changes are persisted in browser
        storage and applied immediately via <code>hasPermission()</code>.
      </Typography>

      {hasAnyUnsaved && (
        <Alert severity="warning" sx={{ mb: 2 }} variant="outlined">
          You have unsaved changes — click <strong>Save</strong> on the edited role to persist them.
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Permissions (preview)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, pr: 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ROLE_ORDER.map((role) => (
              <RoleRow
                key={role}
                role={role}
                draftPerms={draftMap[role]}
                savedPerms={savedMap[role]}
                isEditing={editingRole === role}
                isExpanded={expandedRole === role}
                onToggleExpand={() => handleToggleExpand(role)}
                onStartEdit={() => handleStartEdit(role)}
                onTogglePerm={handleTogglePerm}
                onSave={() => handleSave(role)}
                onCancel={() => handleCancel(role)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block' }}>
        Saved to browser localStorage under key <code>stacktrack_permission_overrides</code>.
        Clearing site data restores defaults. Backend route guards are not affected.
      </Typography>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AccessControlPage;
